import React, { useState, useEffect, useRef } from 'react';
import NumberFlow from '@number-flow/react';
import { GitHubIcon } from '@/components/demos/repo-buttons/github-icon';
import { useGitHubStars } from '@/hooks/use-github-stars';

const GITHUB_REPO = 'enzomanuelmangano/demos';

// Star icon for the count
const StarIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 16 16" fill="currentColor" className={className}>
    <path d="M8 .25a.75.75 0 0 1 .673.418l1.882 3.815 4.21.612a.75.75 0 0 1 .416 1.279l-3.046 2.97.719 4.192a.751.751 0 0 1-1.088.791L8 12.347l-3.766 1.98a.75.75 0 0 1-1.088-.79l.72-4.194L.818 6.374a.75.75 0 0 1 .416-1.28l4.21-.611L7.327.668A.75.75 0 0 1 8 .25Z" />
  </svg>
);

interface Particle {
  x: number;
  y: number;
  size: number;
  speed: number;
  opacity: number;
  rotation: number;
  rotationSpeed: number;
}

const STAR_COLOR = '#a5a5df'; // primary-300
const CANVAS_HEIGHT = 60;

const drawStar = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  rotation: number,
  opacity: number
) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.globalAlpha = opacity;
  ctx.fillStyle = STAR_COLOR;

  // Soft rounded 4-point star
  const r = size;
  ctx.beginPath();
  ctx.moveTo(0, -r);
  ctx.bezierCurveTo(r * 0.2, -r * 0.2, r * 0.2, -r * 0.2, r, 0);
  ctx.bezierCurveTo(r * 0.2, r * 0.2, r * 0.2, r * 0.2, 0, r);
  ctx.bezierCurveTo(-r * 0.2, r * 0.2, -r * 0.2, r * 0.2, -r, 0);
  ctx.bezierCurveTo(-r * 0.2, -r * 0.2, -r * 0.2, -r * 0.2, 0, -r);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
};

const IDLE_OPACITY = 0.8;
const ACTIVE_OPACITY = 1;
const IDLE_SPAWN_INTERVAL = 0.6; // Spawn every 0.6s when idle
const ACTIVE_SPAWN_INTERVAL = 0.08; // Spawn every 0.08s when active

const FloatingStars = ({ isActive }: { isActive: boolean }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number | null>(null);
  const fadeRef = useRef(IDLE_OPACITY);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const width = canvas.offsetWidth;
    const height = canvas.offsetHeight;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    let lastTime = performance.now();
    let spawnTimer = 0;
    let isRunning = true;

    const createParticle = (atY?: number, isIdle?: boolean): Particle => ({
      x: 20 + Math.random() * (width - 40),
      y: atY ?? height,
      size: isIdle ? 2 + Math.random() * 2 : 3 + Math.random() * 3,
      speed: isIdle ? 40 + Math.random() * 30 : 80 + Math.random() * 60,
      opacity: atY ? 0.8 : 0,
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 3,
    });

    // Start with a few idle particles
    if (particlesRef.current.length === 0) {
      for (let i = 0; i < 3; i++) {
        particlesRef.current.push(
          createParticle(height * (0.2 + Math.random() * 0.6), true)
        );
      }
    }

    const animate = (currentTime: number) => {
      if (!isRunning) return;

      const deltaTime = Math.min((currentTime - lastTime) / 1000, 0.1);
      lastTime = currentTime;

      ctx.clearRect(0, 0, width, height);

      // Fade between idle and active opacity
      const targetOpacity = isActive ? ACTIVE_OPACITY : IDLE_OPACITY;
      if (fadeRef.current < targetOpacity) {
        fadeRef.current = Math.min(targetOpacity, fadeRef.current + deltaTime * 4);
      } else {
        fadeRef.current = Math.max(targetOpacity, fadeRef.current - deltaTime * 2);
      }

      // Spawn particles at different rates
      const spawnInterval = isActive ? ACTIVE_SPAWN_INTERVAL : IDLE_SPAWN_INTERVAL;
      spawnTimer += deltaTime;
      if (spawnTimer > spawnInterval) {
        spawnTimer = 0;
        particlesRef.current.push(createParticle(undefined, !isActive));
      }

      particlesRef.current = particlesRef.current.filter((p) => {
        p.y -= p.speed * deltaTime;
        p.rotation += p.rotationSpeed * deltaTime;

        const progress = 1 - p.y / height;
        if (progress < 0.15) {
          p.opacity = Math.min(1, p.opacity + deltaTime * 6);
        } else if (progress > 0.75) {
          p.opacity = Math.max(0, p.opacity - deltaTime * 3);
        }

        const finalOpacity = p.opacity * fadeRef.current;
        if (finalOpacity > 0.01) {
          drawStar(ctx, p.x, p.y, p.size, p.rotation, finalOpacity);
        }

        return p.y > -10;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      isRunning = false;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isActive]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute pointer-events-none"
      style={{
        left: 0,
        right: 0,
        bottom: '100%',
        width: '100%',
        height: CANVAS_HEIGHT,
      }}
    />
  );
};

export const GitHubStarsButton = () => {
  const { data: stars } = useGitHubStars(GITHUB_REPO);
  const [isHovered, setIsHovered] = useState(false);
  const [displayStars, setDisplayStars] = useState(0);

  useEffect(() => {
    if (stars && stars > 0) {
      const timer = setTimeout(() => setDisplayStars(stars), 500);
      return () => clearTimeout(timer);
    }
  }, [stars]);

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <FloatingStars isActive={isHovered} />

      <a
        href={`https://github.com/${GITHUB_REPO}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 sm:gap-2.5 px-3 sm:px-4 py-2 rounded-xl bg-primary/10 border border-primary/20 hover:bg-primary/15 transition-colors duration-200 text-sm text-primary"
      >
        <GitHubIcon className="w-4 h-4" />
        {/* Mobile: just "GitHub" */}
        <span className="sm:hidden">Source code</span>
        {/* Desktop: full text with stars */}
        <span className="hidden sm:inline">Demos is Open Source</span>
        <span className="hidden sm:inline text-primary/20">|</span>
        <span className="hidden sm:inline-flex items-center gap-1 min-w-[3.5rem]">
          <StarIcon className="w-3 h-3" />
          <NumberFlow
            value={displayStars}
            format={{ useGrouping: true }}
            transformTiming={{
              duration: 1500,
              easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
            }}
            spinTiming={{
              duration: 1500,
              easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
            }}
            className="tabular-nums"
          />
        </span>
      </a>
    </div>
  );
};
